import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from '@shared/core';
import { useToast } from '@withjoy/joykit';
import { WarningTriangle } from '@withjoy/joykit/icons';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { addError } from '@shared/utils/logger';
import { usePlaceToStayComponentTelemetry } from '../PlaceToStayComponent.telemetry';
import {
  useAddAccommodationPlaceV2Mutation,
  useUpdateAccommodationPlaceV2Mutation,
  AccommodationPlaceType,
  AccommodationPlaceDisplayLocation,
  Maybe,
  useDeletePageComponentV2Mutation,
  PlaceToStayAccommodationPlaceV2Fragment,
} from '@graphql/generated';
import { CheckIcon } from '@assets/icons';
import { Location } from '@shared/components/LocationInput/LocationInput.controller';
import { LocationInputV2Response } from '@shared/components/LocationInputV2/LocationInputV2.types';
import { HotelInventory } from '@apps/admin/routes/Planning/hooks/HotelListSearcher/useZentrumHubHotelListSearcher';
import { PlaceToStayFormFields, convertToMutationFormat } from '../PlaceToStayForm.types';
import { createPlaceToStayValidationSchema } from '../PlaceToStayComponent.validation';
import { PlaceToStayComponentConfig } from '../PlaceToStayComponent.config';
import { PlaceToStayComponent } from '../PlaceToStayComponent.types';
import { useUpdateWebsitePreview } from '@website-editor/hooks/useUpdateWebsitePreview';
import { useCustomComponentStateContext } from '@website-editor/features/custom-page/context/CustomComponentStateContext/CustomComponentState.context';
import { useCreateNewJsonComponent } from '../../../hooks/useJsonBlobStorageUtils';
import { HotelFormFields } from '../../../../TravelMap/pages/Hotel/HotelForm.types';
import { CustomPropertyFormFields } from '../../../../TravelMap/pages/CustomProperty/CustomPropertyForm.types';
import { useHandleOnSelectHotel } from '../../../../TravelMap/hooks/useHandleOnSelectHotel';
import { convertFormFieldsToAddAccommodationPayload, convertFormFieldsToUpdateAccommodationPayload, getHotelAddress } from '../../../../TravelMap/utils/Accommodations.utils';

interface UsePlaceToStayFormArgs
  extends Readonly<{
    accommodation: PlaceToStayAccommodationPlaceV2Fragment | null;
    accommodationPlaceId: string;
    isEditMode: boolean;
    isOwnContainer: boolean;
    eventHandle: string;
    containerId: string;
    componentDataId?: string;
    goToReorderView: () => void;
    sortOrder?: number;
    pageSlug: string;
  }> {}

const PAGE_NAME = 'Custom page';

export const usePlaceToStayForm = (args: UsePlaceToStayFormArgs) => {
  const { accommodation, accommodationPlaceId, isEditMode, isOwnContainer, eventHandle, containerId, componentDataId, goToReorderView, sortOrder, pageSlug } = args;

  const { tq } = useTranslation('placeToStayComponent');
  const { toast } = useToast();
  const { eventInfo } = useEventInfo();
  const { createNewJsonComponent } = useCreateNewJsonComponent(eventHandle);
  const [deletePageComponentV2] = useDeletePageComponentV2Mutation();
  const { updateWebsitePreview } = useUpdateWebsitePreview();
  const { setRefetchQueries } = useCustomComponentStateContext();
  const telemetry = usePlaceToStayComponentTelemetry();

  const [loading, setLoading] = useState(false);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [matchedHotel, setMatchedHotel] = useState<HotelInventory | null>(null);
  const locationRef = useRef<Location | LocationInputV2Response | null>(null);

  // Error handler for mutations
  const handleError = useEventCallback((error: unknown, source: string) => {
    addError(error, { context: 'PlaceToStayComponentEdit', source });
    toast(tq`Failed to save place to stay. Please try again.`, { icon: <WarningTriangle /> });
    setLoading(false);
  });

  const [addAccommodationPlaceV2] = useAddAccommodationPlaceV2Mutation({
    onError: error => handleError(error, 'addAccommodationPlaceV2'),
    refetchQueries: ['PlaceToStayAccommodations'],
  });

  const [updateAccommodationPlaceV2] = useUpdateAccommodationPlaceV2Mutation({
    onError: error => handleError(error, 'updateAccommodationPlaceV2'),
    refetchQueries: ['PlaceToStayAccommodations'],
  });

  // Create matched hotel inventory for edit mode if applicable
  const matchedHotelInventory: HotelInventory | undefined = useMemo(() => {
    if (!isEditMode || !accommodation || accommodation.type !== 'hotel' || !accommodation.joyPlaceId) {
      return undefined;
    }

    return {
      joyPlaceId: accommodation.joyPlaceId || undefined,
      zhId: accommodation.zhId || '',
      displayName: accommodation.name || '',
      address: accommodation.address || '',
      city: accommodation.city || '',
      state: accommodation.state || '',
      country: accommodation.country || '',
      postalCode: accommodation.postalCode || '',
      starRating: accommodation.starRating || 0,
      photo: accommodation.photo?.url || '',
      photos: accommodation.photo?.url ? [accommodation.photo.url] : [],
      distance: 0,
      location: {
        lat: accommodation.lat || 0,
        lon: accommodation.long || 0,
      },
      ratingCount: 0,
      userRating: 0,
      isPreferredHotel: false,
      hotelUrl: accommodation.url || '',
    } as HotelInventory;
  }, [isEditMode, accommodation]);

  // Set matched hotel in edit mode
  useEffect(() => {
    if (matchedHotelInventory) {
      setMatchedHotel(matchedHotelInventory);
    }
  }, [matchedHotelInventory]);

  // Map accommodation data to form initial values
  const address =
    accommodation?.customAddress ||
    getHotelAddress({
      address: accommodation?.address,
      city: accommodation?.city,
      state: accommodation?.state,
      postalCode: accommodation?.postalCode,
      country: accommodation?.country,
    });

  const initialValues: PlaceToStayFormFields = {
    address: address || '',
    customAddress: address || '',
    name: accommodation?.name || accommodation?.customName || '',
    customName: accommodation?.customName || '',
    link: accommodation?.url || '',
    customUrl: accommodation?.customUrl || '',
    isCustomLinkEnabled: accommodation?.isCustomLinkEnabled ?? true,
    note: accommodation?.userNote || '',
    latitude: accommodation?.lat ?? null,
    longitude: accommodation?.long ?? null,
    googlePlaceId: accommodation?.googlePlaceId ?? null,
    joyPlaceId: accommodation?.joyPlaceId ?? null,
    photoUrl: accommodation?.customPhoto?.url || accommodation?.photo?.url || null,
  };

  const validationSchema = createPlaceToStayValidationSchema(tq);

  const formik = useFormik<PlaceToStayFormFields>({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async values => {
      setLoading(true);

      try {
        if (isEditMode && isOwnContainer && accommodationPlaceId) {
          // Update existing accommodation
          const formValues = convertToMutationFormat(values);
          const updatePayload = convertFormFieldsToUpdateAccommodationPayload(accommodationPlaceId, formValues);
          const result = await updateAccommodationPlaceV2({
            variables: { payload: updatePayload },
          });

          const updatedId = result?.data?.updateAccommodationPlaceV2?.id;
          if (updatedId) {
            toast(tq`Place to stay updated successfully.`, { icon: <CheckIcon /> });
            setRefetchQueries(['PlaceToStayAccommodations']);
            updateWebsitePreview();
            setLoading(false);
            goToReorderView();
          }
        } else {
          // Create new accommodation
          if (!eventInfo?.eventId) {
            handleError(new Error('Event ID is missing'), 'onSubmit - eventInfo');
            return;
          }

          const formValues = convertToMutationFormat(values);
          const addPayload = convertFormFieldsToAddAccommodationPayload(eventInfo.eventId, formValues);

          if (!addPayload.sourceOfData) {
            handleError(new Error('Source of data is missing'), 'onSubmit - addAccommodationPlaceV2');
            return;
          }

          const result = await addAccommodationPlaceV2({
            variables: {
              payload: {
                ...addPayload,
                type: values.joyPlaceId ? AccommodationPlaceType.hotel : AccommodationPlaceType.custom,
                sourceOfData: addPayload.sourceOfData,
                displayLocation: AccommodationPlaceDisplayLocation.CustomPage,
              },
            },
          });

          const newAccommodationId = result?.data?.addAccommodationPlaceV2?.id;
          if (newAccommodationId) {
            const componentData: PlaceToStayComponent = {
              accommodationPlaceId: newAccommodationId,
              sortOrder,
            };

            const jsonResult = await createNewJsonComponent({
              schemaURL: PlaceToStayComponentConfig.schemaUrl,
              type: PlaceToStayComponentConfig.componentType,
              initialData: componentData,
              sortOrder,
              containerId,
            });

            // Delete old component if it exists
            if (componentDataId) {
              await deletePageComponentV2({
                variables: {
                  payload: {
                    id: componentDataId,
                  },
                },
              });
            }

            if (jsonResult?.containerData?.data) {
              toast(tq`Place to stay saved successfully.`, { icon: <CheckIcon /> });
              setRefetchQueries(['PageContainerBySlug', 'PlaceToStayAccommodations']);
              updateWebsitePreview();
              setLoading(false);
              goToReorderView();
            }
          }
        }
      } catch (error) {
        handleError(error, 'onSubmit');
      }
    },
  });

  // Bridge form values to HotelFormFields/CustomPropertyFormFields type
  const updateFormAfterFetchHotelDetails = useEventCallback((updatedValues: HotelFormFields | CustomPropertyFormFields) => {
    // Use values from updatedValues directly, not from stale matchedHotel state
    // This ensures fresh values when a new location is selected
    const joyPlaceId = updatedValues.joyPlaceId || null;
    const isMatch = !!joyPlaceId;

    // Extract search term from location object (original user input, not formatted address)
    // Use location.place for Location type, or location.displayName for LocationInputV2Response
    const location = locationRef.current;
    let searchTerm = '';
    if (location && location.detailedPlace) {
      searchTerm = location.detailedPlace;
    }

    // Only send telemetry if we have a valid search term
    if (searchTerm) {
      // Determine match source: if joyPlaceId exists, match was from Joy catalog; otherwise only Google Places
      const matchSource: 'joyCatalog' | 'googlePlacesOnly' = isMatch ? 'joyCatalog' : 'googlePlacesOnly';

      telemetry.hotelSearchCompleted(PAGE_NAME, {
        isMatch,
        matchSource,
        joyPlaceId: joyPlaceId || null,
        searchTerm,
        pageSlug,
      });
    }

    const mappedValues: PlaceToStayFormFields = {
      address: updatedValues.address || '',
      customAddress: (updatedValues.customAddress as string) || '',
      name: updatedValues.name || '',
      customName: updatedValues.customName || '',
      link: updatedValues.url || '',
      customUrl: updatedValues.customUrl || '',
      // isCustomLinkEnabled is always true for place to stay component
      isCustomLinkEnabled: true,
      note: (updatedValues.userNote as string) || '',
      latitude: updatedValues.latitude || null,
      longitude: updatedValues.longitude || null,
      googlePlaceId: updatedValues.googlePlaceId || null,
      joyPlaceId: joyPlaceId,
      photoUrl: updatedValues.photoUrl || null,
    };
    formik.setValues(mappedValues);
    setLoadingLocation(false);
  });

  const onShowMatchedHotel = useEventCallback((show: boolean, joyPlaceId?: string, hotelInventory?: HotelInventory) => {
    if (show && hotelInventory) {
      setMatchedHotel({ ...hotelInventory, joyPlaceId: joyPlaceId ?? hotelInventory.joyPlaceId });
    } else {
      setMatchedHotel(null);
    }
  });

  const { handleOnSelectHotel } = useHandleOnSelectHotel({
    isHotelForm: false,
    setShowMessage: onShowMatchedHotel,
    setHotelPhoto: (photo: string) => {
      formik.setFieldValue('photoUrl', photo);
    },
    setAutoGeneratedPhoto: (photo: string) => {
      formik.setFieldValue('photoUrl', photo);
    },
    updateFormAfterFetchHotelDetails,
    isUpdateCustomUrlLocally: true,
    isUpdateCustomName: true,
  });

  const handleSearchLocationV1 = useEventCallback((location: unknown) => {
    setLoadingLocation(true);
    setMatchedHotel(null);

    // Capture location object directly to preserve original search term (location.place)
    // This avoids race conditions with form state and ensures we get the actual user input
    locationRef.current = (location as Location | LocationInputV2Response) || null;

    // Convert to compatible type for handleOnSelectHotel - include all required HotelFormFields properties
    // Reset name fields to ensure old values don't persist when selecting a new location
    const updateFormValues = {
      ...formik.values,
      url: '',
      userNote: formik.values.note,
      joyPlaceId: null,
      name: '', // Reset name - will be set from new location data
      customName: '', // Reset customName - will be set from new location data
      isBookingLinkDisabled: true,
      bookingUrl: null,
      checkInDate: null,
      checkOutDate: null,
      cutoffDate: null,
      isJoyManagedRoomBlock: false,
      numberOfRoomsReserved: null,
      pricePerNight: null,
      strikeoutPricePerNight: null,
    };
    // Reset form fields to ensure old values don't persist when selecting a new location
    formik.setValues(updateFormValues);
    handleOnSelectHotel(updateFormValues, location as Maybe<Location>);
  });

  return {
    formik,
    loading,
    loadingLocation,
    matchedHotel,
    handleSearchLocationV1,
    accommodationDistances: accommodation?.distances ?? null,
  };
};
